// Country for sign-up, guessed from the visitor's own device: its time zone first (an iPhone
// in Houston reports America/Chicago even with English-UK language settings), then the
// region in its language setting. Nothing is sent to a third-party IP-lookup service, so the
// visitor's address never leaves the page. It is only a pre-fill — the person can change it.

/** Time zones → ISO 3166-1 alpha-2. Covers the zones our visitors realistically come from. */
const ZONE_TO_COUNTRY = {
  // Africa
  'Africa/Accra': 'GH', 'Africa/Lagos': 'NG', 'Africa/Abidjan': 'CI', 'Africa/Lome': 'TG',
  'Africa/Porto-Novo': 'BJ', 'Africa/Ouagadougou': 'BF', 'Africa/Dakar': 'SN', 'Africa/Bamako': 'ML',
  'Africa/Conakry': 'GN', 'Africa/Freetown': 'SL', 'Africa/Monrovia': 'LR', 'Africa/Banjul': 'GM',
  'Africa/Niamey': 'NE', 'Africa/Douala': 'CM', 'Africa/Nairobi': 'KE', 'Africa/Kampala': 'UG',
  'Africa/Dar_es_Salaam': 'TZ', 'Africa/Kigali': 'RW', 'Africa/Addis_Ababa': 'ET',
  'Africa/Johannesburg': 'ZA', 'Africa/Harare': 'ZW', 'Africa/Lusaka': 'ZM', 'Africa/Maputo': 'MZ',
  'Africa/Gaborone': 'BW', 'Africa/Windhoek': 'NA', 'Africa/Luanda': 'AO', 'Africa/Kinshasa': 'CD',
  'Africa/Cairo': 'EG', 'Africa/Casablanca': 'MA', 'Africa/Algiers': 'DZ', 'Africa/Tunis': 'TN',
  'Africa/Khartoum': 'SD', 'Africa/Mogadishu': 'SO',
  // Americas
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Phoenix': 'US',
  'America/Los_Angeles': 'US', 'America/Anchorage': 'US', 'Pacific/Honolulu': 'US',
  'America/Detroit': 'US', 'America/Indiana/Indianapolis': 'US', 'America/Boise': 'US',
  'America/Kentucky/Louisville': 'US', 'America/Puerto_Rico': 'PR',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA', 'America/Winnipeg': 'CA',
  'America/Halifax': 'CA', 'America/Regina': 'CA', 'America/St_Johns': 'CA',
  'America/Mexico_City': 'MX', 'America/Jamaica': 'JM', 'America/Port_of_Spain': 'TT',
  'America/Barbados': 'BB', 'America/Nassau': 'BS', 'America/Port-au-Prince': 'HT',
  'America/Sao_Paulo': 'BR', 'America/Bogota': 'CO', 'America/Lima': 'PE',
  'America/Argentina/Buenos_Aires': 'AR', 'America/Santiago': 'CL', 'America/Guyana': 'GY',
  // Europe
  'Europe/London': 'GB', 'Europe/Dublin': 'IE', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE',
  'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Madrid': 'ES', 'Europe/Lisbon': 'PT',
  'Europe/Rome': 'IT', 'Europe/Zurich': 'CH', 'Europe/Vienna': 'AT', 'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK', 'Europe/Helsinki': 'FI', 'Europe/Warsaw': 'PL',
  'Europe/Prague': 'CZ', 'Europe/Athens': 'GR', 'Europe/Istanbul': 'TR', 'Europe/Moscow': 'RU',
  'Europe/Kyiv': 'UA', 'Europe/Kiev': 'UA', 'Europe/Bucharest': 'RO', 'Europe/Budapest': 'HU',
  // Asia & Oceania
  'Asia/Dubai': 'AE', 'Asia/Qatar': 'QA', 'Asia/Riyadh': 'SA', 'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK', 'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR',
  'Asia/Singapore': 'SG', 'Asia/Manila': 'PH', 'Asia/Jakarta': 'ID', 'Asia/Bangkok': 'TH',
  'Asia/Jerusalem': 'IL', 'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD', 'Asia/Kuala_Lumpur': 'MY',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU', 'Pacific/Auckland': 'NZ',
};

/** Shown first in the picker: where most of our visitors are. */
export const COMMON_COUNTRIES = ['US', 'GH', 'GB', 'CA', 'NG'];

const ALL_CODES = (
  'AD AE AF AG AI AL AM AO AR AS AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ '
  + 'CA CD CF CG CH CI CK CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM '
  + 'FR GA GB GD GE GH GI GM GN GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE '
  + 'KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MO '
  + 'MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PR PS PT PW PY QA RO RS '
  + 'RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TL TM TN TO TR TT '
  + 'TV TW TZ UA UG US UY UZ VC VE VG VI VN VU WS YE ZA ZM ZW'
).split(' ');

/** Country name in the visitor's language, falling back to the code on very old browsers. */
export function countryName(code) {
  try {
    return new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
}

/** [{ code, name }], common ones first, the rest alphabetical by name. */
export function countryOptions() {
  const rest = ALL_CODES.filter((c) => !COMMON_COUNTRIES.includes(c))
    .map((code) => ({ code, name: countryName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...COMMON_COUNTRIES.map((code) => ({ code, name: countryName(code) })), ...rest];
}

/** Best guess at the visitor's country, or '' when there is nothing to go on. */
export function detectCountry() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone && ZONE_TO_COUNTRY[zone]) return ZONE_TO_COUNTRY[zone];
  } catch { /* fall through to language */ }
  const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
  for (const lang of langs) {
    const region = String(lang || '').split('-')[1];
    if (region && /^[A-Za-z]{2}$/.test(region) && ALL_CODES.includes(region.toUpperCase())) {
      return region.toUpperCase();
    }
  }
  return '';
}
